import React from "react";
import {
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  PinterestShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import styled from "styled-components";
// T22.648: inline data URIs, not static/media files fetched on open -- see
// shareIconSources.js.
import {
  FACEBOOK_ICON_SRC as FacebookIcon,
  LINKEDIN_ICON_SRC as LinkedinIcon,
  MESSENGER_ICON_SRC as MessengerIcon,
  PINTEREST_ICON_SRC as PinterestIcon,
  WHATSAPP_ICON_SRC as WhatsappIcon,
  X_ICON_SRC as TwitterIcon,
} from "./shareIconSources";
import { device } from "../../utils/device";
import { recordShare, SHARE_TYPES } from "./recordShare";

export const Icon = styled.img`
  /* T22.734: 44x44 hit box at every width (was 28x28, a 48x28 button on
     desktop). The 8px padding keeps the visible glyph at 28px. */
  box-sizing: border-box;
  width: 44px;
  height: 44px;
  padding: 8px;
  margin: 0;
  cursor: pointer;
  border-radius: 50%;

  /* T22.223: phone touch target 44x44 (was 22x22 inside a 42x22 hit box);
     the 6px padding keeps the visible glyph at 32px. */
  @media (${device.mobilePortrait}) {
    box-sizing: border-box;
    width: 44px;
    height: 44px;
    padding: 6px;
    margin: 0;
  }
`;

export const CopyIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  /* T22.734: 44x44 hit box at every width (was 28x28 on desktop); the grey
     disc stays 28px via padding + content-box background. */
  box-sizing: border-box;
  width: 44px;
  height: 44px;
  padding: 8px;
  margin: 0;
  cursor: pointer;
  border-radius: 50%;
  background-color: #f0f2f5;
  background-clip: content-box;

  /* T22.223: same 44x44 phone touch target as the share icons. */
  @media (${device.mobilePortrait}) {
    width: 44px;
    height: 44px;
    padding: 0;
    margin: 0;
  }
`;

const ShareButton = ({
  ShareComponent,
  url,
  quote,
  media,
  description,
  IconSrc,
  appId,
  // Share counting: the entity being shared and which network this button is.
  // Optional -- when the caller omits shareId, recordShare's own id guard
  // (isValidWheelId) fails closed and nothing is posted, so a non-wheel
  // caller (e.g. a Collection) never mislabels an event with the wrong id.
  shareId,
  shareType,
}) => (
  <ShareComponent
    url={url}
    title={quote}
    media={media}
    description={description}
    appId={appId} // Only used where applicable, e.g., Messenger
    beforeOnClick={() => {
      recordShare(shareId, shareType);
    }}
  >
    <Icon src={IconSrc} alt="share-icon" />
  </ShareComponent>
);

/**
 * The 6-network share row (Facebook, Pinterest, LinkedIn, WhatsApp, X,
 * Messenger) + copy-link icon. Originally lived only inside the Wheel
 * ShareModal; ISP-9999 (collection-share-modal-match-wheel) pulled it out so
 * a Collection's Share dialog offers the SAME channel set instead of a
 * copy-link-only fork -- see CollectionShareModal.jsx. Renders no wrapper of
 * its own; the caller supplies the layout (IconGrid / V2IconRow / a Box) so
 * this stays presentation-agnostic between the two very differently-chromed
 * modals.
 */
export default function ShareChannelIcons({
  url,
  quote,
  media,
  description,
  shareId,
  onCopy,
  copyAriaLabel = "Copy link to clipboard",
}) {
  return (
    <>
      <ShareButton
        ShareComponent={FacebookShareButton}
        shareId={shareId}
        shareType={SHARE_TYPES.FACEBOOK}
        url={url}
        quote={quote}
        IconSrc={FacebookIcon}
        appId="1237547604287422" // Replace with your valid Facebook App ID
      />
      <ShareButton
        ShareComponent={PinterestShareButton}
        shareId={shareId}
        shareType={SHARE_TYPES.PINTEREST}
        url={url}
        media={media} // Pinterest requires a valid media URL
        description={description}
        IconSrc={PinterestIcon}
      />
      <ShareButton
        ShareComponent={LinkedinShareButton}
        shareId={shareId}
        shareType={SHARE_TYPES.LINKEDIN}
        url={url}
        quote={quote}
        IconSrc={LinkedinIcon}
      />
      <ShareButton
        ShareComponent={WhatsappShareButton}
        shareId={shareId}
        shareType={SHARE_TYPES.WHATSAPP}
        url={url}
        quote={quote}
        IconSrc={WhatsappIcon}
      />
      <ShareButton
        ShareComponent={TwitterShareButton}
        shareId={shareId}
        shareType={SHARE_TYPES.X}
        url={url}
        quote={quote}
        IconSrc={TwitterIcon}
      />
      <ShareButton
        ShareComponent={FacebookMessengerShareButton}
        shareId={shareId}
        shareType={SHARE_TYPES.MESSENGER}
        url={url}
        appId="1237547604287422" // Replace with your valid Facebook App ID
        IconSrc={MessengerIcon}
      />
      <CopyIconWrapper
        role="button"
        tabIndex={0}
        aria-label={copyAriaLabel}
        onClick={onCopy}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onCopy?.();
          }
        }}
      >
        <ContentCopyIcon fontSize="18px" />
      </CopyIconWrapper>
    </>
  );
}
